/**
 * React hooks for user profiles
 * Integrates with TanStack Query for caching and state management
 */

import { useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import * as userProfilesService from '../services/userProfiles';
import { inviteUser, bulkCreateUsers } from '../services/invites';
import type { UserRole, UserProfile } from '../types/database';
import type {
  BulkCreateUser,
  BulkCreateResult,
} from '../types/bulk-import.types';

// Query keys for consistent cache management
export const userProfileKeys = {
  all: ['userProfile'] as const,
  currentRoot: () => [...userProfileKeys.all, 'current'] as const,
  current: (userId: string) =>
    [...userProfileKeys.currentRoot(), userId] as const,
  byId: (userId: string) => [...userProfileKeys.all, 'id', userId] as const,
  directory: () => [...userProfileKeys.all, 'directory'] as const,
  export: () => [...userProfileKeys.all, 'export'] as const,
};

/**
 * Fetch the current authenticated user's profile
 * Automatically cached by TanStack Query
 * Only fetches when user is authenticated
 */
export function useUserProfile() {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: userProfileKeys.current(user?.id || ''),
    queryFn: () => userProfilesService.getUserProfile(user?.id),
    enabled: isAuthenticated && !!user,
    // User profile rarely changes - use aggressive caching
    staleTime: 5 * 60 * 1000, // 5 minutes - profile data is fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache
    refetchOnMount: false, // Don't refetch on mount - use cached data
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: false, // Don't refetch on reconnect
  });

  // Invalidate cache when user changes (logout/login)
  useEffect(() => {
    if (!isAuthenticated || !user) {
      // Clear cache when user logs out
      queryClient.removeQueries({ queryKey: userProfileKeys.currentRoot() });
    }
  }, [isAuthenticated, user, queryClient]);

  // Memoize the user role to avoid recalculating on every render
  const userRole = useMemo((): UserRole | null => {
    if (!query.data) {
      return null;
    }
    return (query.data.role as UserRole) || null;
  }, [query.data]);

  // Helper function to get the user's role
  const getUserRole = useCallback((): UserRole | null => {
    return userRole;
  }, [userRole]);

  // Helper function to check if user is admin
  const isAdmin = useCallback((): boolean => {
    return userRole === 'admin';
  }, [userRole]);

  // Helper function to check if user has a specific role
  const hasRole = useCallback(
    (role: UserRole): boolean => {
      return userRole === role;
    },
    [userRole]
  );

  // Helper function to check if user has any of the specified roles
  const hasAnyRole = useCallback(
    (...roles: UserRole[]): boolean => {
      return userRole !== null && roles.includes(userRole);
    },
    [userRole]
  );

  return {
    ...query,
    profile: query.data,
    getUserRole,
    isAdmin,
    hasRole,
    hasAnyRole,
  };
}

/**
 * Fetch a user profile by ID (useful for admin views)
 */
export function useUserProfileById(userId: string | null) {
  return useQuery({
    queryKey: userProfileKeys.byId(userId || ''),
    queryFn: () => {
      if (!userId) {
        throw new Error('User ID is required');
      }
      return userProfilesService.getUserProfileById(userId);
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Fetch all user profiles (sorted alphabetically)
 */
export function useAllUserProfiles() {
  return useQuery({
    queryKey: userProfileKeys.directory(),
    queryFn: userProfilesService.listUserProfiles,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
}

/**
 * Fetch all user profiles on demand for CSV export.
 */
export function useUsersForExport() {
  return useQuery({
    queryKey: userProfileKeys.export(),
    queryFn: userProfilesService.listUserProfiles,
    enabled: false,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * Update a user's role (admin only)
 */
export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: UserRole }) =>
      userProfilesService.updateUserRole(userId, role),
    onSuccess: (updatedProfile, variables) => {
      // Optimistically update the directory cache with the updated profile
      queryClient.setQueryData(
        userProfileKeys.directory(),
        (oldData: UserProfile[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((user) =>
            user.id === variables.userId ? updatedProfile : user
          );
        }
      );
      // Update the specific user profile cache if it exists
      queryClient.setQueryData(
        userProfileKeys.byId(variables.userId),
        updatedProfile
      );
      // If updating current user's role, invalidate current profile cache
      queryClient.invalidateQueries({
        queryKey: userProfileKeys.currentRoot(),
      });
    },
  });
}

/**
 * Update multiple users' roles (admin only)
 */
export function useUpdateUserRolesBulk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userIds, role }: { userIds: string[]; role: UserRole }) =>
      userProfilesService.updateUserRolesBulk(userIds, role),
    onSuccess: (updatedProfiles) => {
      if (updatedProfiles.length === 0) {
        return;
      }

      const updatedById = new Map(
        updatedProfiles.map((profile) => [profile.id, profile])
      );

      queryClient.setQueryData(
        userProfileKeys.directory(),
        (oldData: UserProfile[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((user) => updatedById.get(user.id) ?? user);
        }
      );

      updatedProfiles.forEach((profile) => {
        queryClient.setQueryData(userProfileKeys.byId(profile.id), profile);
      });

      queryClient.invalidateQueries({
        queryKey: userProfileKeys.currentRoot(),
      });
    },
  });
}

/**
 * Update a user's event visibility permission (admin only)
 * Allows non-admin users to see all events regardless of their role
 */
export function useUpdateUserEventVisibility() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      canSeeAllEvents,
    }: {
      userId: string;
      canSeeAllEvents: boolean;
    }) =>
      userProfilesService.updateUserEventVisibility(userId, canSeeAllEvents),
    onSuccess: (updatedProfile, variables) => {
      // Optimistically update the directory cache with the updated profile
      queryClient.setQueryData(
        userProfileKeys.directory(),
        (oldData: UserProfile[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((user) =>
            user.id === variables.userId ? updatedProfile : user
          );
        }
      );
      // Update the specific user profile cache if it exists
      queryClient.setQueryData(
        userProfileKeys.byId(variables.userId),
        updatedProfile
      );
      // If updating current user's permission, invalidate current profile cache
      queryClient.invalidateQueries({
        queryKey: userProfileKeys.currentRoot(),
      });
    },
  });
}

/**
 * Delete a user (admin only)
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => userProfilesService.deleteUser(userId),
    onSuccess: (_, userId) => {
      // Invalidate directory list to refresh user list
      queryClient.invalidateQueries({
        queryKey: userProfileKeys.directory(),
      });
      // Remove the specific user profile from cache
      queryClient.removeQueries({
        queryKey: userProfileKeys.byId(userId),
      });
    },
  });
}

/**
 * Delete multiple users (admin only)
 */
export function useDeleteUsersBulk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userIds: string[]) =>
      userProfilesService.deleteUsersBulk(userIds),
    onSuccess: (_, userIds) => {
      queryClient.invalidateQueries({
        queryKey: userProfileKeys.directory(),
      });
      userIds.forEach((userId) => {
        queryClient.removeQueries({
          queryKey: userProfileKeys.byId(userId),
        });
      });
    },
  });
}

/**
 * Invite a user (admin only)
 */
export function useInviteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: inviteUser,
    onSuccess: () => {
      // Invalidate directory list to refresh user list
      queryClient.invalidateQueries({
        queryKey: userProfileKeys.directory(),
      });
    },
  });
}

/**
 * Bulk create users (admin only)
 */
export function useBulkCreateUsers() {
  const queryClient = useQueryClient();

  return useMutation<BulkCreateResult, Error, BulkCreateUser[]>({
    mutationFn: bulkCreateUsers,
    onSuccess: () => {
      // Invalidate directory list to refresh user list
      queryClient.invalidateQueries({
        queryKey: userProfileKeys.directory(),
      });
    },
  });
}
